import {useCallback, useEffect, useState} from "react";
import {getBackupById} from "@/api/BackupApi.ts";
import {Backup} from "@/backup/Backup.ts";
import {isAxiosError} from "axios";

export function useBackupDetail(backupId: string | undefined) {
    const [backup, setBackup] = useState<Backup | null>(null);
    const [loadedId, setLoadedId] = useState<string | null>(null);
    const [error, setError] = useState(false);

    const refresh = useCallback(async () => {
        if (!backupId) return;
        try {
            setBackup(await getBackupById(backupId));
            setError(false);
        } catch (reason) {
            if (isAxiosError(reason) && reason.response?.status === 404) {
                setBackup(null);
                setError(false);
            }
            else setError(true);
        } finally {
            setLoadedId(backupId);
        }
    }, [backupId]);

    useEffect(() => {
        let cancelled = false;
        if (!backupId) return;

        getBackupById(backupId)
            .then(result => {
                if (!cancelled) {
                    setBackup(result);
                    setError(false);
                }
            })
            .catch(reason => {
                if (!cancelled) {
                    if (isAxiosError(reason) && reason.response?.status === 404) {
                        setBackup(null);
                        setError(false);
                    }
                    else setError(true);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadedId(backupId);
            });
        return () => {
            cancelled = true;
        };
    }, [backupId]);

    return {
        backup: backup?.id === backupId ? backup : null,
        loading: Boolean(backupId && loadedId !== backupId),
        error: Boolean(backupId && loadedId === backupId && error),
        refresh,
    };
}
